import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import PWAInstallPrompt from './components/common/PWAInstallPrompt';
import PWAUpdatePrompt from './components/common/PWAUpdatePrompt';
import Layout from './components/Layout';
import AdminLayout from './components/admin/AdminLayout';
import Auth from './components/Auth';
import AuthCallback from './pages/AuthCallback';
import ModernHome from './pages/ModernHome';
import Models from './pages/Models';
import DashboardModels from './pages/DashboardModels';
import Training from './pages/Training';

import ProblemDiagnosis from './pages/ProblemDiagnosis';
import AIAssistant from './pages/AIAssistant';
import Profile from './pages/Profile';
import AdminDashboard from './pages/admin/AdminDashboard';
import TagManagement from './pages/admin/TagManagement';
import AdminModelManagement from './pages/admin/ModelManagement';
import ContentManagementPage from './pages/admin/ContentManagementPage';
import AIPromptsPage from './pages/admin/AIPromptsPage';
import AIPromptEdit from './pages/admin/AIPromptEdit';
import ModelVendorManagementPage from './pages/admin/ModelVendorManagement';
import InsightManagement from './pages/admin/InsightManagement';
import OfficialAnalysisEdit from './pages/admin/OfficialAnalysisEdit';
import ScrapeManagement from './pages/admin/ScrapeManagement';
import ScrapeTaskDataView from './pages/admin/ScrapeTaskDataView';
import ScrapeDataDetailView from './pages/admin/ScrapeDataDetailView';
import LearningManagement from './pages/admin/LearningManagement';
import AIManagement from './pages/admin/AIManagement';
import TestAIScenario from './pages/TestAIScenario';
import OptimizedAnalysisPage from './pages/OptimizedAnalysisPage';
import AnalysisListPage from './pages/AnalysisListPage';
import OfficialAnalysisPreview from './pages/OfficialAnalysisPreview';
import TestAnalysisPage from './pages/TestAnalysisPage';
import AIScenarioEdit from './pages/admin/AIScenarioEdit';
import DebugPage from './pages/DebugPage';
import LearningArticlePreview from './pages/LearningArticlePreview';
import LearningArticleListPage from './pages/LearningArticleListPage';
import LearningArticleEditPage from './pages/admin/LearningArticleEditPage';


const AppContent: React.FC = () => {
  const { user, loading } = useAuth();
  
  console.log('🚀 AppContent渲染:', { hasUser: !!user, loading, userEmail: user?.email });

  if (loading) {
    console.log('⏳ App显示加载状态，等待认证完成');
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <p className="ml-4 text-gray-600">正在加载应用...</p>
      </div>
    );
  }

  console.log('✅ App认证完成，开始渲染路由');

  return (
    <Routes>
      {/* 首页路由 - 所有用户都可以访问 */}
      <Route path="/" element={<ModernHome />} />
      
      {/* 案例列表页面 - 公开可访问 */}
      <Route path="/analyses" element={<AnalysisListPage />} />
      
      {/* 分析页面 - 公开可访问，服务器端可索引，使用SEO友好的slug */}
      <Route path="/analysis/:slug" element={<OptimizedAnalysisPage />} />
      
      {/* 官方深度分析预览页面 - 公开可访问，使用SEO友好的slug */}
      <Route path="/official-analysis/:slug" element={<OfficialAnalysisPreview />} />
      
      {/* 兼容旧的ID路由，重定向到slug路由 */}
      <Route path="/official-analysis-id/:id" element={<OfficialAnalysisPreview />} />
      
      {/* 测试页面 */}
      <Route path="/test-analysis" element={<TestAnalysisPage />} />
      
      {/* 调试页面 - 用于排查管理员权限问题 */}
      <Route path="/debug" element={user ? <DebugPage /> : <Auth />} />
      
      {/* 认证相关路由 */}
      <Route path="/auth" element={<Auth />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* 模型工厂 - 公开可访问 */}
      <Route path="/models" element={<Models />} />
      <Route path="/models/:id" element={<Models />} />
      
      {/* 控制台内的模型工厂 - 需要登录，带侧边栏 */}
      <Route path="/dashboard/models" element={user ? <Layout><DashboardModels /></Layout> : <Auth />} />
      <Route path="/dashboard/models/:id" element={user ? <Layout><DashboardModels /></Layout> : <Auth />} />
      
      {/* 需要登录的功能页面 */}
      <Route path="/training" element={user ? <Layout><Training /></Layout> : <Auth />} />

      <Route path="/problem-diagnosis" element={user ? <Layout><ProblemDiagnosis /></Layout> : <Auth />} />
      <Route path="/ai-assistant" element={user ? <Layout><AIAssistant /></Layout> : <Auth />} />
      <Route path="/profile" element={user ? <Layout><Profile /></Layout> : <Auth />} />
      
      {/* 管理员页面 */}
      <Route path="/admin" element={user ? <Layout><AdminDashboard /></Layout> : <Auth />} />
      <Route path="/admin/content-management" element={user ? <ContentManagementPage /> : <Auth />} />
      <Route path="/admin/models" element={user ? <AdminLayout><AdminModelManagement /></AdminLayout> : <Auth />} />
      <Route path="/admin/prompts" element={user ? <AIPromptsPage /> : <Auth />} />
      <Route path="/admin/prompts/edit/:id" element={user ? <AIPromptEdit /> : <Auth />} />
      <Route path="/admin/prompts/new" element={user ? <AIPromptEdit /> : <Auth />} />
      <Route path="/admin/model-vendors" element={user ? <ModelVendorManagementPage /> : <Auth />} />
      <Route path="/admin/insights" element={user ? <InsightManagement /> : <Auth />} />
      <Route path="/admin/insights/edit/:id" element={user ? <Layout><OfficialAnalysisEdit /></Layout> : <Auth />} />
      <Route path="/admin/scrape" element={user ? <ScrapeManagement /> : <Auth />} />
      <Route path="/admin/scrape/data/:taskId" element={user ? <ScrapeTaskDataView /> : <Auth />} />
      <Route path="/admin/scrape/data/:taskId/detail/:dataId" element={user ? <ScrapeDataDetailView /> : <Auth />} />
      <Route path="/admin/learning" element={user ? <LearningManagement /> : <Auth />} />
      <Route path="/admin/ai" element={user ? <AdminLayout><AIManagement /></AdminLayout> : <Auth />} />
      <Route path="/admin/scenarios/edit/:id" element={user ? <AIScenarioEdit /> : <Auth />} />
      <Route path="/admin/scenarios/new" element={user ? <AIScenarioEdit /> : <Auth />} />
      <Route path="/test-ai-scenario" element={user ? <TestAIScenario /> : <Auth />} />
      
      {/* 学习文章相关页面 - 公开可访问 */}
      <Route path="/learning/articles" element={<LearningArticleListPage />} />
      <Route path="/learning/article/:id" element={<LearningArticlePreview />} />
      
      {/* 文章编辑页面 */}
      <Route path="/admin/learning/article/edit/:id" element={user ? <LearningArticleEditPage /> : <Auth />} />
      <Route path="/admin/learning/article/new" element={user ? <LearningArticleEditPage /> : <Auth />} />

    </Routes>
  );
};

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <Router>
        <AppContent />
        <PWAInstallPrompt />
        <PWAUpdatePrompt />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#10B981',
                secondary: '#fff',
              },
            },
            error: {
              duration: 4000,
              iconTheme: {
                primary: '#EF4444',
                secondary: '#fff',
              },
            },
          }}
        />
        </Router>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;